import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from 'react-i18next';
import axios from "../../axiosConfig";
import TrainerLayout from "./TrainerLayout";
import "../../styles/Exercises.css";
import { TrainerContext } from "../../context/TrainerContext";
import AddExerciseModal from "./AddExerciseModal";
import EditExerciseModal from "./EditExerciseModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import ExerciseItem from "./ExerciseItem";
import SearchBar from "../../components/commons/searchbar/SearchBar";

const Exercises = () => {
    const { t } = useTranslation(['exercises', 'common']);
    const { trainer } = useContext(TrainerContext);
    const [exercises, setExercises] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [expandedId, setExpandedId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [selectedExercise, setSelectedExercise] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    useEffect(() => {
        if (!trainer?.schoolId) return;

        const fetchExercises = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem("jwt");
                let endpoint = `/api/v1/exercise/findAll/${trainer.schoolId}?page=${currentPage}&size=${pageSize}`;

                if (searchTerm.trim()) {
                    endpoint += `&search=${encodeURIComponent(searchTerm.trim())}`;
                    setIsSearching(true);
                } else {
                    setIsSearching(false);
                }

                const response = await axios.get(endpoint, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setExercises(response.data.content);
                setTotalPages(response.data.totalPages || 1);
            } catch (err) {
                setError(t('exercises:fetchError'));
            } finally {
                setLoading(false);
            }
        };

        const timeoutId = setTimeout(() => {
            fetchExercises();
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [trainer, currentPage, pageSize, searchTerm, refreshTrigger]);

    const toggleExpand = (id) => {
        setExpandedId(expandedId === id ? null : id);
    };

    const openEditModal = (exercise) => {
        setSelectedExercise(exercise);
        setShowEditModal(true);
    };

    const openDeleteModal = (exercise) => {
        setSelectedExercise(exercise);
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setSelectedExercise(null);
    };

    const handleDeleteExercise = async () => {
        if (!selectedExercise) return;

        try {
            const token = localStorage.getItem("jwt");
            await axios.delete(`/api/v1/exercise/${selectedExercise.exerciseId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setExercises(exercises.filter(ex => ex.exerciseId !== selectedExercise.exerciseId));
            closeDeleteModal();
        } catch (err) {
            setError(t('exercises:deleteError'));
            closeDeleteModal();
        }
    };

    const editExercise = (updatedExercise) => {
        setExercises(exercises.map(ex => ex.exerciseId === updatedExercise.exerciseId ? updatedExercise : ex));
        setShowEditModal(false);
    };

    const handleExerciseAdded = () => {
        setCurrentPage(0);
        setRefreshTrigger(prev => prev + 1);
    };

    return (
        <TrainerLayout>
            <div className="exercises-container">
                <h1 className="exercises-mobile-title">{t('exercises:title')}</h1>

                <div className="exercises-search-section">
                    <div className="exercises-search-and-button-group">
                        <SearchBar
                            width="400px"
                            value={searchTerm}
                            onChange={setSearchTerm}
                            onSearch={() => setCurrentPage(0)}
                            placeholder={{
                                mobile: t('common:search'),
                                desktop: t('exercises:searchPlaceholder')
                            }}
                        />

                        <button
                            className="exercises-add-button"
                            onClick={() => setIsModalOpen(true)}
                        >
                            {t('exercises:addExercise')}
                        </button>
                    </div>

                    <div className="exercises-pagination-controls-inline">
                        <label className="exercises-page-size-label">{t('common:perPage')}:</label>
                        <select
                            className="exercises-page-size-select"
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setCurrentPage(0);
                            }}
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={30}>30</option>
                        </select>
                    </div>
                </div>

                {isSearching && exercises.length > 0 && (
                    <div className="exercises-search-results-info">
                        {t('exercises:searchResultsCount', { count: exercises.length })}
                    </div>
                )}

                {loading ? (
                    <p className="exercises-loading-text">{t('common:loading')}</p>
                ) : error ? (
                    <p className="exercises-error-message">{error}</p>
                ) : (
                    <div className="exercise-list">
                        {exercises.length === 0 ? (
                            <div className="no-exercises-message">
                                {isSearching
                                    ? t('exercises:noSearchResults')
                                    : t('exercises:noExercises')
                                }
                            </div>
                        ) : (
                            exercises.map((exercise) => (
                                <ExerciseItem
                                    key={exercise.exerciseId}
                                    exercise={exercise}
                                    expanded={expandedId === exercise.exerciseId}
                                    toggleExpand={() => toggleExpand(exercise.exerciseId)}
                                    onEditClick={openEditModal}
                                    onDeleteClick={openDeleteModal}
                                />
                            ))
                        )}
                    </div>
                )}

                {isModalOpen && (
                    <AddExerciseModal
                        onClose={() => setIsModalOpen(false)}
                        onSuccess={handleExerciseAdded}
                    />
                )}

                <DeleteConfirmationModal
                    isOpen={showDeleteModal}
                    onClose={closeDeleteModal}
                    onConfirm={handleDeleteExercise}
                    itemName={selectedExercise?.name || ''}
                />

                {showEditModal && selectedExercise && (
                    <EditExerciseModal
                        exercise={selectedExercise}
                        onClose={() => setShowEditModal(false)}
                        onSave={editExercise}
                    />
                )}

                <div className="exercises-pagination-section">
                    <button
                        className="exercises-pagination-btn"
                        disabled={currentPage === 0}
                        onClick={() => setCurrentPage(currentPage - 1)}
                    >
                        {t('common:previous')}
                    </button>
                    <span className="exercises-page-info">
                        {t('common:pageInfo', { current: currentPage + 1, total: totalPages })}
                    </span>
                    <button
                        className="exercises-pagination-btn"
                        disabled={currentPage + 1 >= totalPages}
                        onClick={() => setCurrentPage(currentPage + 1)}
                    >
                        {t('common:next')}
                    </button>
                </div>
            </div>
        </TrainerLayout>
    );
};

export default Exercises;